/**
 * Fernet (AES-128-CBC + HMAC-SHA256) over the Web Crypto API.
 *
 * Wire-compatible with Python's `cryptography.fernet.Fernet`, which is what
 * app/core/payload_crypto.py uses on the backend. Implemented here rather than
 * pulled from npm because the token format is ~40 lines and a crypto dependency
 * is a supply-chain surface we do not need to take on.
 *
 * Token layout (all big-endian, concatenated then urlsafe-base64'd):
 *   0x80          version, 1 byte
 *   timestamp     8 bytes, seconds since epoch
 *   iv            16 bytes
 *   ciphertext    variable, AES-128-CBC with PKCS#7 padding
 *   hmac          32 bytes, HMAC-SHA256 over everything above
 *
 * The 32-byte key splits in half: the FIRST 16 bytes sign, the LAST 16 encrypt.
 *
 * NOTE ON SECRECY: the key lives in the frontend bundle, so this is not a
 * confidentiality boundary against anyone who opens devtools. See the header of
 * app/core/payload_crypto.py for what it does and does not buy.
 *
 * Requires a secure context — `crypto.subtle` is undefined on plain http for
 * anything other than localhost.
 */
import env from 'config/env';

const VERSION = 0x80;
const IV_LENGTH = 16;
const HMAC_LENGTH = 32;
const HEADER_LENGTH = 1 + 8 + IV_LENGTH; // version + timestamp + iv

const bytesFromBase64Url = (value) => {
  const padded = value.padEnd(value.length + ((4 - (value.length % 4)) % 4), '=');
  const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
  const out = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) out[i] = binary.charCodeAt(i);
  return out;
};

const base64UrlFromBytes = (bytes) => {
  let binary = '';
  // Chunked to avoid blowing the argument limit on large bodies.
  for (let i = 0; i < bytes.length; i += 0x8000) {
    binary += String.fromCharCode.apply(null, bytes.subarray(i, i + 0x8000));
  }
  // Padding is KEPT: Python's base64.urlsafe_b64decode rejects unpadded input.
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_');
};

let keyPromise = null;

/** Import the signing and encryption halves once, then reuse them. */
const getKeys = () => {
  if (keyPromise) return keyPromise;
  keyPromise = (async () => {
    const raw = bytesFromBase64Url(env.payloadEncryptionKey);
    if (raw.length !== 32) {
      throw new Error(
        `REACT_APP_PAYLOAD_ENCRYPTION_KEY must decode to 32 bytes, got ${raw.length}.`,
      );
    }
    const [signingKey, encryptionKey] = [raw.slice(0, 16), raw.slice(16, 32)];
    return {
      hmac: await crypto.subtle.importKey(
        'raw', signingKey, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign', 'verify'],
      ),
      aes: await crypto.subtle.importKey(
        'raw', encryptionKey, { name: 'AES-CBC' }, false, ['encrypt', 'decrypt'],
      ),
    };
  })();
  return keyPromise;
};

export const isPayloadEncryptionAvailable = () =>
  Boolean(
    env.payloadEncryptionEnabled
      && env.payloadEncryptionKey
      && typeof crypto !== 'undefined'
      && crypto.subtle,
  );

/** Encrypt a UTF-8 string into a Fernet token. */
export const encryptToToken = async (plaintext) => {
  const { hmac, aes } = await getKeys();
  const iv = crypto.getRandomValues(new Uint8Array(IV_LENGTH));
  const ciphertext = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-CBC', iv }, aes, new TextEncoder().encode(plaintext)),
  );

  const signed = new Uint8Array(HEADER_LENGTH + ciphertext.length);
  signed[0] = VERSION;
  // Fernet's timestamp is 8 bytes big-endian. Written as two 32-bit halves
  // rather than setBigUint64 so the file needs no BigInt (the project's eslint
  // target predates it) — the high word stays 0 until the year 2106.
  const seconds = Math.floor(Date.now() / 1000);
  const view = new DataView(signed.buffer);
  view.setUint32(1, Math.floor(seconds / 4294967296), false);
  view.setUint32(5, seconds >>> 0, false);
  signed.set(iv, 9);
  signed.set(ciphertext, HEADER_LENGTH);

  const mac = new Uint8Array(await crypto.subtle.sign('HMAC', hmac, signed));
  const token = new Uint8Array(signed.length + HMAC_LENGTH);
  token.set(signed);
  token.set(mac, signed.length);
  return base64UrlFromBytes(token);
};

/** Verify and decrypt a Fernet token back to a UTF-8 string. */
export const decryptToken = async (token) => {
  const { hmac, aes } = await getKeys();
  const raw = bytesFromBase64Url(token);
  if (raw.length < HEADER_LENGTH + HMAC_LENGTH || raw[0] !== VERSION) {
    throw new Error('Malformed encrypted payload.');
  }

  const signed = raw.subarray(0, raw.length - HMAC_LENGTH);
  const mac = raw.subarray(raw.length - HMAC_LENGTH);
  // Authenticate BEFORE decrypting: feeding unverified ciphertext to AES-CBC is
  // what makes padding-oracle attacks possible.
  if (!(await crypto.subtle.verify('HMAC', hmac, mac, signed))) {
    throw new Error('Payload signature verification failed.');
  }

  const iv = signed.subarray(9, HEADER_LENGTH);
  const ciphertext = signed.subarray(HEADER_LENGTH);
  const plaintext = await crypto.subtle.decrypt({ name: 'AES-CBC', iv }, aes, ciphertext);
  return new TextDecoder().decode(plaintext);
};

/** Wrap a JS value as the {"data": "<token>"} envelope the backend expects. */
export const encryptEnvelope = async (value) => ({
  data: await encryptToToken(JSON.stringify(value)),
});

/** Unwrap {"data": "<token>"} back to the original JS value. */
export const decryptEnvelope = async (envelope) =>
  JSON.parse(await decryptToken(envelope.data));

const payloadCrypto = { encryptEnvelope, decryptEnvelope, isPayloadEncryptionAvailable };

export default payloadCrypto;
